import { Bad } from '@core/model/framework/Result/BadResult';
import { OkVal } from '@core/model/framework/Result/OkResult';
import { Usecase } from '@core/model/framework/usecase';
import type {
  WaitOnWebauthNotificationGateway,
  WebauthCompletedConnection,
} from '@core/model/gateway/waitOnWebauthNotificationGateway';
import { SERVICE_ID } from '@di/serviceId';
import { inject, injectable } from 'inversify';

export type WaitOnWebauthNotificationInput = {
  userToken: string;
  /*
    The correlation key the origin page chose when it started the journey and
    sent on webauth-url (itself, or through the relay fragment). The backend
    remembered it on the journey: polling its status with it tells how the
    journey ended — succeeded with the connection, or failed with a reason —
    whatever device the bank came back on.
  */
  journeyKey: string;
};

export type WaitOnWebauthNotificationOutput = Promise<
  | OkVal<WebauthCompletedConnection>
  | Bad<
      'WEBAUTH_JOURNEY_FAILED' | 'WAITED_NEW_BANK_CONNECTION_FOR_TOO_LONG' | 'STOPPED_POLLING_VIA_USER_ACTION'
    >
>;

@injectable()
export class WaitOnWebauthNotification
  implements Usecase<WaitOnWebauthNotificationInput, WaitOnWebauthNotificationOutput>
{
  constructor(
    @inject(SERVICE_ID.waitOnWebauthNotificationGateway)
    private readonly waitOnWebauthNotificationGateway: WaitOnWebauthNotificationGateway
  ) {}

  execute(input: WaitOnWebauthNotificationInput): WaitOnWebauthNotificationOutput {
    return this.waitOnWebauthNotificationGateway.awaitNewBankConnection(input);
  }

  stop(): void {
    return this.waitOnWebauthNotificationGateway.stopWaitingNewBankConnection();
  }
}
